import type { ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import styles from './parts.module.css';

/** Building blocks of the user wizard's steps that the frames repeat (onboarding.md A2). */

export interface ProgressRow {
  label: string;
  state: 'done' | 'active' | 'open';
}

/** „Fortschritts-Checkzeile": ✓ on success-bg, the accent spinner, the dashed open circle. */
export function ProgressRows({
  rows,
  variant = 'plain',
}: {
  rows: readonly ProgressRow[];
  variant?: 'plain' | 'boxed';
}) {
  return (
    <ul className={clsx(styles.rows, variant === 'boxed' && styles.rowsBoxed)}>
      {rows.map((row) => (
        <li key={row.label} className={clsx(styles.row, styles[row.state])}>
          <span className={styles.mark} aria-hidden="true">
            {row.state === 'done' ? '✓' : ''}
          </span>
          <span>{row.label}</span>
          <span className={styles.srOnly}>
            {row.state === 'done' ? ' · erledigt' : row.state === 'active' ? ' · läuft' : ' · offen'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The 6-px bar of the frames (inset well, accent fill). */
export function Bar({ percent, label }: { percent: number; label: string }) {
  return (
    <div
      className={styles.bar}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      <div className={styles.barFill} style={{ width: `${percent}%` }} />
    </div>
  );
}

export interface ChoiceCardProps {
  icon: ReactNode;
  title: string;
  text: string;
  badge?: string | undefined;
  selected: boolean;
  onSelect: () => void;
  compact?: boolean | undefined;
}

/** Nutzer 04 / M3: provider card (radio) — selected = accent-soft with the accent border. */
export function ChoiceCard({ icon, title, text, badge, selected, onSelect, compact }: ChoiceCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={clsx(styles.choice, selected && styles.choiceSelected, compact && styles.choiceCompact)}
      onClick={onSelect}
    >
      <span className={styles.choiceIcon} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.choiceText}>
        <span className={styles.choiceTitle}>{title}</span>
        <span className={styles.choiceSub}>{text}</span>
      </span>
      {badge ? <span className={styles.badge}>{badge}</span> : null}
    </button>
  );
}

/** White card of the frames (radius card, hairline, shadow-2). */
export function Panel({
  children,
  className,
  tone,
}: {
  children: ReactNode;
  className?: string | undefined;
  tone?: 'danger' | 'accent' | undefined;
}) {
  return (
    <div
      className={clsx(
        styles.panel,
        tone === 'danger' && styles.panelDanger,
        tone === 'accent' && styles.panelAccent,
        className,
      )}
    >
      {children}
    </div>
  );
}

/** The 10-px uppercase eyebrow of the frames' cards. */
export function CardEyebrow({ children }: { children: ReactNode }) {
  return <span className={styles.eyebrow}>{children}</span>;
}
